import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import api from '../services/api';

export const DEFAULT_BLOCK_TYPES = [
    { id: 'sprint',     label: 'Sprint',      color: '#FF6B6B' },
    { id: 'deep-focus', label: 'Deep Focus',  color: '#9B59B6' },
    { id: 'break',      label: 'Break',       color: '#3498DB' },
    { id: 'check-ins',  label: 'Check-ins',   color: '#F39C12' },
    { id: 'tend',       label: 'Tend',        color: '#2ECC71' },
    { id: 'meeting',    label: 'Meeting',     color: '#C0C0C0' },
    { id: 'onbench',    label: 'On Bench',    color: '#FFA500' }
];

function formatDate(date) {
    return format(date, 'yyyy-MM-dd');
}

export function useTimeblock() {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [dayData, setDayData] = useState({ scheduledBlocks: [], sessions: [] });
    const [activeTimer, setActiveTimer] = useState(null); // { blockId, startTime, resumeAccumulatedMs? }
    const [elapsed, setElapsed] = useState(0); // seconds (includes resumed time when same task paused then restarted)
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [lastPaused, setLastPaused] = useState(null); // { blockTypeId, durationMs } — for resume-same-task

    const intervalRef = useRef(null);
    const dayDataRef = useRef(dayData);
    dayDataRef.current = dayData;

    // Tick elapsed timer (current segment + any resumed accumulated time)
    useEffect(() => {
        if (activeTimer) {
            const computeElapsed = () => {
                const segmentSecs = Math.floor((Date.now() - new Date(activeTimer.startTime).getTime()) / 1000);
                const resumedSecs = Math.floor((activeTimer.resumeAccumulatedMs || 0) / 1000);
                return resumedSecs + segmentSecs;
            };
            setElapsed(computeElapsed()); // show resumed time immediately, no 0 flash
            intervalRef.current = setInterval(() => setElapsed(computeElapsed()), 500);
        } else {
            clearInterval(intervalRef.current);
            setElapsed(0);
        }
        return () => clearInterval(intervalRef.current);
    }, [activeTimer]);

    const loadDay = useCallback(async (date) => {
        setLoading(true);
        try {
            const dateStr = formatDate(date);
            const res = await api.get(`/timeblocks/${dateStr}`);
            setDayData({
                scheduledBlocks: res.data.scheduledBlocks || [],
                sessions: res.data.sessions || []
            });
        } catch (err) {
            console.error('Failed to load timeblock day:', err);
            setDayData({ scheduledBlocks: [], sessions: [] });
        } finally {
            setLoading(false);
        }
    }, []);

    // Load day whenever selectedDate changes; stop any running timer and clear resume state
    useEffect(() => {
        setActiveTimer(null);
        setLastPaused(null);
        loadDay(selectedDate);
    }, [selectedDate, loadDay]);

    const saveDay = useCallback(async (date, data) => {
        setSaving(true);
        try {
            const dateStr = formatDate(date);
            await api.put(`/timeblocks/${dateStr}`, data);
        } catch (err) {
            console.error('Failed to save timeblock day:', err);
        } finally {
            setSaving(false);
        }
    }, []);

    const mutate = useCallback((updater) => {
        setDayData(prev => {
            const next = updater(prev);
            saveDay(selectedDate, next);
            return next;
        });
    }, [selectedDate, saveDay]);

    const addBlock = useCallback((blockTypeId, startTime, endTime) => {
        const block = { id: uuidv4(), blockTypeId, startTime, endTime };
        mutate(prev => ({
            ...prev,
            scheduledBlocks: [...prev.scheduledBlocks, block]
                .sort((a, b) => a.startTime.localeCompare(b.startTime))
        }));
    }, [mutate]);

    const editBlock = useCallback((id, changes) => {
        mutate(prev => ({
            ...prev,
            scheduledBlocks: prev.scheduledBlocks
                .map(b => b.id === id ? { ...b, ...changes } : b)
                .sort((a, b) => a.startTime.localeCompare(b.startTime))
        }));
    }, [mutate]);

    const deleteBlock = useCallback((id) => {
        if (activeTimer?.blockId === id) {
            setActiveTimer(null);
        }
        mutate(prev => ({
            ...prev,
            scheduledBlocks: prev.scheduledBlocks.filter(b => b.id !== id)
        }));
    }, [mutate, activeTimer]);

    const deleteSession = useCallback((sessionId) => {
        mutate(prev => ({
            ...prev,
            sessions: prev.sessions.filter(s => s.id !== sessionId)
        }));
    }, [mutate]);

    const updateSession = useCallback((sessionId, { blockTypeId, startTime, endTime }) => {
        mutate(prev => ({
            ...prev,
            sessions: prev.sessions
                .map(s =>
                    s.id === sessionId
                        ? { ...s, ...(blockTypeId != null && { blockTypeId }), ...(startTime != null && { startTime }), ...(endTime != null && { endTime }) }
                        : s
                )
                .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        }));
    }, [mutate]);

    const addSession = useCallback(({ blockTypeId, startTime, endTime }) => {
        const session = {
            id: uuidv4(),
            blockId: null,
            blockTypeId,
            startTime,
            endTime
        };
        mutate(prev => ({
            ...prev,
            sessions: [...prev.sessions, session].sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        }));
    }, [mutate]);

    const clearSessions = useCallback(() => {
        mutate(prev => ({ ...prev, sessions: [] }));
    }, [mutate]);

    const loadTemplate = useCallback((templateBlocks) => {
        const blocks = templateBlocks.map(b => ({ ...b, id: uuidv4() }));
        mutate(prev => ({
            ...prev,
            scheduledBlocks: blocks.sort((a, b) => a.startTime.localeCompare(b.startTime))
        }));
    }, [mutate]);

    const startTimer = useCallback((blockId) => {
        const blocks = dayDataRef.current.scheduledBlocks;
        const blockTypeId = blocks.find(b => b.id === blockId)?.blockTypeId ?? blockId;
        const isResumingSame = lastPaused?.blockTypeId === blockTypeId;
        const resumeAccumulatedMs = isResumingSame ? lastPaused.durationMs : 0;
        if (isResumingSame) setLastPaused(null);
        setActiveTimer({
            blockId,
            startTime: new Date().toISOString(),
            ...(resumeAccumulatedMs > 0 && { resumeAccumulatedMs })
        });
    }, [lastPaused]);

    const stopTimer = useCallback(() => {
        if (!activeTimer) return;
        const scheduledBlock = dayDataRef.current.scheduledBlocks.find(b => b.id === activeTimer.blockId);
        const blockTypeId = scheduledBlock?.blockTypeId ?? activeTimer.blockId;
        const now = Date.now();
        const startMs = new Date(activeTimer.startTime).getTime();
        const segmentMs = now - startMs;
        const totalAccumulatedMs = (activeTimer.resumeAccumulatedMs || 0) + segmentMs;

        const session = {
            id: uuidv4(),
            blockId: activeTimer.blockId,
            blockTypeId,
            startTime: activeTimer.startTime,
            endTime: new Date(now).toISOString()
        };
        setLastPaused({ blockTypeId, durationMs: totalAccumulatedMs });
        setActiveTimer(null);
        mutate(prev => ({
            ...prev,
            sessions: [...prev.sessions, session]
        }));
    }, [activeTimer, mutate]);

    return {
        selectedDate,
        setSelectedDate,
        dayData,
        activeTimer,
        elapsed,
        loading,
        saving,
        addBlock,
        editBlock,
        deleteBlock,
        deleteSession,
        updateSession,
        addSession,
        clearSessions,
        loadTemplate,
        startTimer,
        stopTimer,
        loadDay
    };
}
